import { z } from "zod";

import {
  parseFloatValue,
  parseIntValue,
  parseToDateValue,
} from "@/features/FormCard/utils";

import {
  AutocompleteField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { dateSchema } from "@/features/FormCard/utils";

import { choice_yes_no } from "@/data/choice.ts";
import { counterparty } from "@/data/counterparty.ts";
import { country } from "@/data/country.ts";
import { nace_2 } from "@/data/nace_2.ts";
import { rating_fitch } from "@/data/rating_fitch.ts";
import { rating_moodys } from "@/data/rating_moodys.ts";
import { rating_sp } from "@/data/rating_sp.ts";
import { EditForm } from "@/features/Edit/types.ts";
import i18n from "@/i18n";

export const a7FIAIssuerTsEditForm: EditForm = {
  table_name: "a7_fia_issuer_ts",
  fieldsNotApplyTranslation: [
    "type_de_contrepartie",
    "actionnaire_majoritaire_public",
    "notation_moodys_contrepartie",
    "notation_fitch_contrepartie",
    "notation_sp_contrepartie",
    "sous_jacent_en_defaut",
  ],
  formSchema: z.object({
    date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
    a7_fia_issuer_id: z.preprocess(
      (val) => parseInt(val as string, 10),
      z.number().int(),
    ),
    secteur_nace: z.string().nullable(),
    code_iso_du_pays: z.string().nullable(),
    type_de_contrepartie: z.string().nullable(),
    notation_moodys_contrepartie: z.string().nullable(),
    notation_fitch_contrepartie: z.string().nullable(),
    notation_sp_contrepartie: z.string().nullable(),
    chiffre_d_affaires_du_sous_jacent: z.preprocess(
      (val) => parseFloatValue(val as string),
      z.number().nullable(),
    ),
    nb_employes_contrepartie: z.preprocess(
      (val) => parseIntValue(val as string),
      z.number().int().nullable(),
    ),
    actionnaire_majoritaire_public: z.string().nullable(),
    cqs_contrepartie: z.preprocess(
      (val) => parseIntValue(val as string),
      z.number().int().nullable(),
    ),
    sous_jacent_en_defaut: z.string().nullable(),
  }),
  formBodyContent: (form, multiFieldOptions) => {
    return (
      <div className="gap-2">
        <div className="grid grid-cols-4 gap-2">
          <DatePickerField
            name="date"
            label={i18n.t("a7_fia_issuer_ts.columns.date", {
              ns: "tables",
              defaultValue: "Date",
            })}
            isRequired={true}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <InputField
            name="a7_fia_issuer_id"
            label={i18n.t("a7_fia_issuer_ts.columns.a7_fia_issuer_name", {
              ns: "tables",
              defaultValue: "Issuer",
            })}
            isRequired={true}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <AutocompleteField
            name="secteur_nace"
            label={i18n.t("a7_fia_issuer_ts.columns.secteur_nace", {
              ns: "tables",
              defaultValue: "Sector",
            })}
            className="col-span-2"
            options={nace_2.sort((n1, n2) =>
              (n1.value ?? "").localeCompare(n2.value ?? ""),
            )}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <SelectField
            name="type_de_contrepartie"
            label={i18n.t("a7_fia_issuer_ts.columns.type_de_contrepartie", {
              ns: "tables",
              defaultValue: "Counterparty type",
            })}
            className="col-span-2"
            options={counterparty.map((c) => ({
              value: c.value,
              label: c.value,
            }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <InputField
            name="chiffre_d_affaires_du_sous_jacent"
            label={i18n.t(
              "a7_fia_issuer_ts.columns.chiffre_d_affaires_du_sous_jacent",
              {
                ns: "tables",
                defaultValue: "Revenue",
              },
            )}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <InputField
            name="nb_employes_contrepartie"
            label={i18n.t("a7_fia_issuer_ts.columns.nb_employes_contrepartie", {
              ns: "tables",
              defaultValue: "Employees",
            })}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <SelectField
            name="actionnaire_majoritaire_public"
            label={i18n.t(
              "a7_fia_issuer_ts.columns.actionnaire_majoritaire_public",
              {
                ns: "tables",
                defaultValue: "Majority shareholder",
              },
            )}
            options={choice_yes_no.map((c) => ({
              value: c.value,
              label: c.value,
            }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <AutocompleteField
            name="code_iso_du_pays"
            label={i18n.t("a7_fia_issuer_ts.columns.code_iso_du_pays", {
              ns: "tables",
              defaultValue: "Country ISO code",
            })}
            options={country}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <InputField
            name="cqs_contrepartie"
            label={i18n.t("a7_fia_issuer_ts.columns.cqs_contrepartie", {
              ns: "tables",
              defaultValue: "CQS",
            })}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
        </div>
        <div className="grid grid-cols-4 gap-2 pt-2">
          <SelectField
            name="notation_moodys_contrepartie"
            label={i18n.t(
              "a7_fia_issuer_ts.columns.notation_moodys_contrepartie",
              {
                ns: "tables",
                defaultValue: "Moody's rating",
              },
            )}
            options={rating_moodys.map((c) => ({
              value: c.value,
              label: c.value,
            }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <SelectField
            name="notation_fitch_contrepartie"
            label={i18n.t(
              "a7_fia_issuer_ts.columns.notation_fitch_contrepartie",
              {
                ns: "tables",
                defaultValue: "Fitch rating",
              },
            )}
            options={rating_fitch.map((c) => ({
              value: c.value,
              label: c.value,
            }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <SelectField
            name="notation_sp_contrepartie"
            label={i18n.t("a7_fia_issuer_ts.columns.notation_sp_contrepartie", {
              ns: "tables",
              defaultValue: "S&P rating",
            })}
            options={rating_sp.map((c) => ({ value: c.value, label: c.value }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <SelectField
            name="sous_jacent_en_defaut"
            label={i18n.t("a7_fia_issuer_ts.columns.sous_jacent_en_defaut", {
              ns: "tables",
              defaultValue: "Default",
            })}
            options={choice_yes_no.map((c) => ({
              value: c.value,
              label: c.value,
            }))}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
        </div>
      </div>
    );
  },
};
